// Story 1.16 — Top-up modal: amount picker + hosted-checkout handoff.
//
// UX flow 5: suggested amount chips (€10/€25/€50/€100), custom-amount
// input, Continue → POST /wallet/topup-intent.
//
// Two provider outcomes:
//   * completed=true — mock provider credited the balance at intent
//     creation. Toast + close + onSuccess. This is the path beta users
//     take (money handling is mocked in the closed beta).
//   * completed=false + checkout_url — open the provider-hosted payment
//     page in a NEW TAB (a full redirect would break the interrupt-resume
//     contract with BuyDossierPage/ShopBuyModal), then poll the wallet
//     balance every 5s for up to 60s until it changes. Card entry and any
//     SCA challenge happen on the provider's page, never in the SPA.
//
// Top-up is 1:1 (product decision 2026-08-03): you pay exactly the
// top-up amount and receive exactly that balance. No fee rows, no
// effective-rate hints — platform fees live on the sale side.

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  cn,
} from "@greenhold/components";
import toast from "react-hot-toast";

import { attachRequestIdToScope } from "../../lib/api-client";
import { useTopUpIntent } from "./useTopUpIntent";

const MIN_CENTS = 500;
const MAX_CENTS = 1_000_000;
const SUGGESTED_AMOUNTS_CENTS = [1_000, 2_500, 5_000, 10_000] as const;

// Same cadence as OnboardingPanel's post-handoff poll: 5s ticks, ~60s cap.
const POLL_INTERVAL_MS = 5_000;
const POLL_DURATION_MS = 60_000;

function formatEur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const euros = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}€${euros}.${remainder.toString().padStart(2, "0")}`;
}

async function fetchBalanceCents(): Promise<number> {
  const response = await fetch("/api/v1/wallet/balance", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) throw new Error(`balance fetch failed: ${response.status}`);
  const body = (await response.json()) as { balance_cents: number };
  return body.balance_cents;
}

type CheckoutState = {
  url: string;
  amountCents: number;
};

type TopUpModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Story 2.10 — fires AFTER the balance is credited + the modal closes
   * itself. Used by BuyDossierPage to re-open the original
   * PurchaseConfirmModal with the freshly credited balance (top-up
   * interrupt + resume per UX flow 2 + UX-DR §1201-§1205).
   *
   * Backward-compat: undefined (the existing WalletPage usage) is a no-op.
   */
  onSuccess?: () => void;
  /**
   * Story 1.36 D1 — fires when the top-up hits 412
   * `wallet_details_required` (identity form not yet completed). The
   * caller brings the user to the wallet-details form; the modal closes.
   * Undefined is a graceful no-op (the error still surfaces as a toast).
   */
  onRequiresWalletDetails?: () => void;
};

export function TopUpModal({
  open,
  onOpenChange,
  onSuccess,
  onRequiresWalletDetails,
}: TopUpModalProps) {
  const [amountCents, setAmountCents] = useState<number>(SUGGESTED_AMOUNTS_CENTS[1]);
  const [customInput, setCustomInput] = useState<string>("");
  const [checkout, setCheckout] = useState<CheckoutState | null>(null);
  const [polling, setPolling] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  // Balance snapshot taken before the checkout tab opens; the poll
  // declares success the moment the balance differs from it. Null until
  // the first successful read (baseline fetch failures self-heal on the
  // next tick).
  const baselineCentsRef = useRef<number | null>(null);
  const pollExpiresAtRef = useRef<number>(0);
  const topupIntent = useTopUpIntent();
  const queryClient = useQueryClient();

  // Reset state on modal close — fresh idempotency key per submit.
  useEffect(() => {
    if (!open) {
      setCheckout(null);
      setPolling(false);
      setTimedOut(false);
      baselineCentsRef.current = null;
      setCustomInput("");
      setAmountCents(SUGGESTED_AMOUNTS_CENTS[1]);
    }
  }, [open]);

  const isValid = amountCents >= MIN_CENTS && amountCents <= MAX_CENTS;

  function handleCustomInputChange(raw: string) {
    setCustomInput(raw);
    const num = parseFloat(raw);
    if (Number.isFinite(num) && num > 0) {
      setAmountCents(Math.round(num * 100));
    }
  }

  function handleCredited(creditedAmountCents: number) {
    setPolling(false);
    queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
    toast.success(
      `Top-up of €${(creditedAmountCents / 100).toFixed(2)} confirmed!`,
    );
    onOpenChange(false);
    onSuccess?.();
  }

  // Post-handoff balance poll. The provider webhook credits the balance
  // server-side once the hosted checkout finishes; any change from the
  // pre-checkout baseline means the money landed.
  useEffect(() => {
    if (!polling) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const cents = await fetchBalanceCents();
        if (cancelled) return;
        if (baselineCentsRef.current === null) {
          baselineCentsRef.current = cents;
        } else if (cents !== baselineCentsRef.current) {
          handleCredited(checkout?.amountCents ?? 0);
          return;
        }
      } catch {
        // Transient fetch failure — keep polling until the window closes.
      }
      if (!cancelled && Date.now() >= pollExpiresAtRef.current) {
        setPolling(false);
        setTimedOut(true);
      }
    };
    const id = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling]);

  function startPollWindow() {
    pollExpiresAtRef.current = Date.now() + POLL_DURATION_MS;
    setTimedOut(false);
    setPolling(true);
  }

  async function handleContinue() {
    if (!isValid) return;
    const key = crypto.randomUUID();
    topupIntent.mutate(
      { amount_cents: amountCents, idempotencyKey: key },
      {
        onSuccess: async (data) => {
          if (data.completed) {
            // Mock provider credited the balance at intent creation —
            // there is no checkout step. useTopUpIntent already
            // invalidated the balance query.
            toast.success(
              `Top-up of €${(amountCents / 100).toFixed(2)} added to your balance.`,
            );
            onOpenChange(false);
            onSuccess?.();
            return;
          }
          if (!data.checkout_url) {
            toast.error("Payment could not be started. Try again.");
            return;
          }
          // Snapshot the balance BEFORE the user can pay, so the poll's
          // change-detection has a pre-payment baseline.
          try {
            baselineCentsRef.current = await fetchBalanceCents();
          } catch {
            baselineCentsRef.current = null;
          }
          window.open(data.checkout_url, "_blank", "noopener,noreferrer");
          setCheckout({ url: data.checkout_url, amountCents });
          startPollWindow();
        },
        onError: (err) => {
          // 412 PRECONDITION_FAILED — wallet details not yet completed.
          // Don't dump the raw error: close the modal and hand the user to
          // the identity form, then they retry the top-up once it's saved.
          if (err.code === "wallet_details_required") {
            onOpenChange(false);
            onRequiresWalletDetails?.();
            toast(
              "Complete your wallet details first, then come back to add funds.",
              { duration: 6_000 },
            );
            return;
          }
          toast.error(err.message || "Top-up failed. Try again.");
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-0 overflow-hidden border border-brand-gold-500/40 bg-brand-parchment p-0 text-brand-ink-900 shadow-xl sm:rounded-xl">
        {/* Shared header band — one DialogTitle for every DOM state (amount
            view and checkout-wait view), so the title stays fixed while the
            body swaps. */}
        <DialogHeader className="space-y-1.5 border-b border-brand-gold-500/40 bg-brand-parchment-hi px-6 pb-5 pr-10 pt-6 text-left">
          <p className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-brand-forest-700">
            Greenhold · Vault Ledger
          </p>
          <DialogTitle className="font-heading text-2xl font-semibold leading-tight tracking-tight text-brand-ink-900">
            Add funds to your vault
          </DialogTitle>
          <DialogDescription className="font-body text-sm text-brand-ink-700">
            Fund your balance to claim Certificates of Patronage — top-ups are
            1:1, no fees.
          </DialogDescription>
        </DialogHeader>

        {checkout === null ? (
          <div className="space-y-5 px-6 pb-6 pt-6">
            {/* Hero readout — the amount you're committing, live + unmissable */}
            <div className="flex flex-col items-center gap-1.5">
              <span className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-brand-forest-700">
                You&apos;re adding
              </span>
              <span className="font-mono text-5xl font-bold leading-none tabular-nums text-brand-ink-900">
                {formatEur(amountCents)}
              </span>
              <span
                className="mt-1 h-0.5 w-12 rounded-full bg-brand-gold-300"
                aria-hidden="true"
              />
            </div>

            {/* Suggested amounts — embossed ledger tokens */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-brand-forest-700">
                  Choose an amount
                </span>
                <span
                  className="h-px flex-1 bg-brand-gold-500/40"
                  aria-hidden="true"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {SUGGESTED_AMOUNTS_CENTS.map((cents) => {
                  const selected =
                    amountCents === cents && customInput === "";
                  return (
                    <button
                      key={cents}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setAmountCents(cents);
                        setCustomInput("");
                      }}
                      className={cn(
                        "relative flex items-center justify-center rounded-lg border px-4 py-4 font-mono text-lg font-semibold tabular-nums transition-all duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-parchment",
                        selected
                          ? "border-brand-gold-500 bg-brand-forest-900 text-brand-gold-200 shadow-md ring-1 ring-inset ring-brand-gold-300/50"
                          : "border-brand-gold-500/40 bg-brand-paper-50 text-brand-ink-900 hover:border-brand-gold-500 hover:bg-brand-parchment-hi",
                      )}
                    >
                      {selected && (
                        <span
                          className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full bg-brand-gold-300 shadow-[0_0_0_2px_rgba(14,42,31,0.9)]"
                          aria-hidden="true"
                        />
                      )}
                      {formatEur(cents)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom amount */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Label
                  htmlFor="custom-amount"
                  className="font-mono text-[11px] font-medium uppercase tracking-wider text-brand-forest-700"
                >
                  Or enter a custom amount
                </Label>
                <span
                  className="h-px flex-1 bg-brand-gold-500/40"
                  aria-hidden="true"
                />
              </div>
              <div className="relative">
                <span
                  className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 font-mono text-lg font-semibold text-brand-forest-700"
                  aria-hidden="true"
                >
                  €
                </span>
                <Input
                  id="custom-amount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="5"
                  max="10000"
                  placeholder="0.00"
                  value={customInput}
                  onChange={(e) => handleCustomInputChange(e.target.value)}
                  className={cn(
                    "h-12 rounded-lg border-brand-gold-500/50 bg-brand-paper-50 pl-9 pr-4 font-mono text-lg font-semibold tabular-nums text-brand-ink-900 placeholder:font-normal placeholder:text-brand-ink-700/70 focus-visible:border-brand-forest-500 focus-visible:ring-brand-forest-500/30",
                    customInput !== "" &&
                      "border-brand-forest-500 ring-1 ring-brand-forest-500/30",
                  )}
                />
              </div>
              <p className="font-mono text-[11px] tracking-wide text-brand-forest-700">
                €5 minimum · €10,000 maximum
              </p>
            </div>

            {/* Validation */}
            {!isValid && (
              <p
                role="alert"
                className="flex items-center gap-2 rounded-md border border-state-error-fill/30 bg-brand-paper-50 px-3 py-2 font-mono text-xs font-semibold text-state-error-fill"
              >
                <span aria-hidden="true">⚠</span>
                {amountCents < MIN_CENTS
                  ? "Minimum top-up is €5.00."
                  : "Maximum top-up is €10,000.00."}
              </p>
            )}

            {/* Continue */}
            <Button
              type="button"
              variant="primary"
              disabled={!isValid || topupIntent.isPending}
              onClick={handleContinue}
              className="h-12 w-full rounded-lg font-mono text-sm font-semibold uppercase tracking-[0.14em] shadow-md hover:shadow-lg"
            >
              {topupIntent.isPending ? "Preparing…" : "Continue to payment"}
            </Button>

            {/* Story 11.4 — the Steam-style generic line (owner ruling on W24,
                2026-09-01). It states NO amount, names NO tax, claims NO
                registration, and is true in a zero case — which is the phase
                the platform is actually in. Matched word-for-word to the tone
                of the served ToS §5(1), which Story 11.6 rewrote to
                "Prices are shown in EUR, VAT included where applicable. The
                price you see is the price you pay — nothing is added at
                checkout."

                ⛔ THE RULE IT ENCODES: never state a VAT amount when VAT is
                zero. Do not "improve" this into a VAT line, a rate, or a
                breakdown — a specific tax asserted here is a statement a
                business that is not VAT-registered cannot make.
                `TopUpModal.test.tsx` pins both halves. */}
            <p
              data-testid="topup-vat-note"
              className="text-center font-body text-xs text-brand-ink-700"
            >
              VAT included where applicable — the amount you see is the amount
              you pay.
            </p>
          </div>
        ) : (
          <div className="space-y-5 px-6 pb-6 pt-6">
            {/* Ledger recap — what the checkout tab is charging */}
            <div className="flex items-baseline justify-between rounded-lg border border-brand-gold-500/40 bg-brand-parchment-hi px-4 py-3">
              <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-brand-forest-700">
                Paying in new tab
              </span>
              <span className="font-mono text-xl font-bold tabular-nums text-brand-forest-700">
                {formatEur(checkout.amountCents)}
              </span>
            </div>

            {timedOut ? (
              <>
                <p
                  data-testid="topup-poll-timeout"
                  className="font-body text-sm text-brand-ink-700"
                >
                  Still waiting for our payment partner to confirm. If you
                  finished paying, give it a moment and check again — or
                  contact support if this persists.
                </p>
                <Button
                  type="button"
                  variant="primary"
                  onClick={startPollWindow}
                  className="h-12 w-full rounded-lg font-mono text-sm font-semibold uppercase tracking-[0.14em] shadow-md hover:shadow-lg"
                >
                  Check again
                </Button>
              </>
            ) : (
              <p
                data-testid="topup-poll-waiting"
                className="font-body text-sm text-brand-ink-700"
              >
                Complete your payment in the tab we just opened. This dialog
                closes by itself once the funds land — usually within a few
                seconds.
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                window.open(checkout.url, "_blank", "noopener,noreferrer");
              }}
              className="font-body text-sm underline decoration-dotted text-brand-forest-700"
            >
              Reopen the payment page
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
